// Immunocal SG — Product showcase controller  (rev 2026-07-06)
// Tab switching between collection panels + a PAGED product carousel. Each page
// shows N cards (the CSS `--sc-cols`, 3 on desktop) and slides via transform.
// The lead lifestyle image lives outside the clipping viewport, so it stays
// fixed while the products page — matching the live site.

class CustomProductShowcase extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('[data-sc-panel]'));
    this.tabsWrap = this.querySelector('[data-sc-tabs]');
    this.prevBtn = this.querySelector('[data-sc-prev]');
    this.nextBtn = this.querySelector('[data-sc-next]');
    this.dotsWrap = this.querySelector('[data-sc-dots]');
    if (this.panels.length === 0) return;

    this.active = 0;
    this.page = 0;
    this.buildTabs();
    this.setupSubscribe();

    this.prevBtn?.addEventListener('click', () => this.go(this.page - 1));
    this.nextBtn?.addEventListener('click', () => this.go(this.page + 1));

    // Mobile is a native scroll-snap slider (smooth finger swiping). Keep the
    // active page + dots in sync as the user scrolls. Each panel has its own
    // scroll viewport, so bind them all.
    this.querySelectorAll('[data-sc-viewport]').forEach((vp) =>
      vp.addEventListener('scroll', () => this.onScroll(vp), { passive: true })
    );

    // Recompute page geometry whenever the element is resized (window resize,
    // theme-editor panel toggles, font/image reflow).
    if ('ResizeObserver' in window) {
      this._ro = new ResizeObserver(() => this.layout());
      this._ro.observe(this);
    } else {
      window.addEventListener('resize', () => this.layout(), { passive: true });
    }

    this.selectTab(this.firstProductPanel());
    this.classList.add('sc-ready');
    // Re-measure once images/fonts settle.
    window.addEventListener('load', () => this.layout(), { once: true });
  }

  buildTabs() {
    this.tabs = [];
    if (!this.tabsWrap || this.panels.length <= 1) return;
    this.panels.forEach((panel, i) => {
      const link = panel.dataset.link;
      // A tab with a link is a real anchor that navigates on click; a tab with a
      // collection is a button that switches the visible products panel.
      const btn = document.createElement(link ? 'a' : 'button');
      btn.className = 'showcase__tab';
      btn.setAttribute('role', 'tab');
      btn.textContent = panel.dataset.label || `Tab ${i + 1}`;
      if (link) {
        btn.href = link;
      } else {
        btn.type = 'button';
        btn.addEventListener('click', () => this.selectTab(i));
      }
      this.tabsWrap.appendChild(btn);
      this.tabs.push(btn);
    });
  }

  // Index of the first tab that actually shows products (skips link-only tabs).
  firstProductPanel() {
    const i = this.panels.findIndex((p) => !p.dataset.link);
    return i < 0 ? 0 : i;
  }

  // Subscribe & save: ticking a card's checkbox enables the selling_plan hidden
  // input on that card's add-to-cart form, so the product is added as a
  // subscription. Unticking disables it (a normal one-time purchase).
  setupSubscribe() {
    this.addEventListener('change', (e) => {
      const cb = e.target;
      if (!cb || !cb.classList || !cb.classList.contains('sc-card__sub-input')) return;
      const card = cb.closest('.sc-card');
      const sp = card && card.querySelector('.sc-card__selling-plan');
      if (sp) sp.disabled = !cb.checked;
    });
  }

  get panel() { return this.panels[this.active]; }
  get viewport() { return this.panel?.querySelector('[data-sc-viewport]'); }
  get track() { return this.panel?.querySelector('[data-sc-track]'); }
  get cards() {
    return this.track ? Array.from(this.track.children).filter((c) => c.classList.contains('sc-card')) : [];
  }

  // Cards per page + total pages + px step for one card, from live layout.
  metrics() {
    const vp = this.viewport;
    const cards = this.cards;
    if (!vp || cards.length === 0) return { perPage: 1, pages: 1, step: 0, gap: 0 };
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '20') || 20;
    const cardW = cards[0].getBoundingClientRect().width || 1;
    const step = cardW + gap;
    // Page by the number of FULLY-visible cards (floor, not round). This way a
    // "peek" layout (e.g. 1.5 cards on phones) advances exactly 1 card per tap
    // instead of rounding up to 2 and skipping the peeked card. The +0.02 epsilon
    // keeps whole-fill layouts (2.999… -> 3) from floored down to 2.
    const perPage = Math.max(1, Math.floor((vp.clientWidth + gap) / step + 0.02));
    const pages = Math.max(1, Math.ceil(cards.length / perPage));
    return { perPage, pages, step, gap };
  }

  go(p, animate = true) {
    const track = this.track;
    const vp = this.viewport;
    if (!track || !vp) return;
    const { perPage, pages, step } = this.metrics();
    this.page = Math.min(Math.max(p, 0), pages - 1);

    // Page by WHOLE pages and do NOT clamp to the track end. The live site is a
    // windowed carousel that slices the list into pages of `perPage` — the last
    // page shows just the remaining cards (e.g. 8 products -> 3,3,2). Clamping the
    // final page flush would repeat the previous page's last card (a visible
    // duplicate), so we mirror live's partial last page instead.
    const x = this.page * perPage * step;

    // Mobile: the viewport is a native scroll-snap slider — scroll to the page
    // (smooth momentum) instead of the transform/fade used on desktop. The
    // browser clamps the scroll target to the real max, so partial last pages
    // land flush.
    if (this.isNativeScroll()) {
      vp.scrollTo({ left: x, behavior: animate ? 'smooth' : 'auto' });
      this.updateControls(pages);
      return;
    }

    // The transform is not transitioned (only opacity is), so it repositions
    // instantly. Between pages we cross-fade: fade out, jump, fade back in —
    // so there's no horizontal slide (matches the live site).
    const place = () => { track.style.transform = `translateX(${-x}px)`; };
    clearTimeout(this._fadeT);
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!animate || reduce) {
      place();
      track.style.opacity = '1';
    } else {
      track.style.opacity = '0';
      this._fadeT = setTimeout(() => { place(); track.style.opacity = '1'; }, 200);
    }

    this.updateControls(pages);
  }

  // On mobile the carousel is a native scroll-snap slider; on desktop it's a
  // transform/fade pager.
  isNativeScroll() {
    return window.matchMedia('(max-width: 991px)').matches;
  }

  // Keep the active page + dots in sync while the user swipes the native slider.
  onScroll(vp) {
    if (!this.isNativeScroll() || vp !== this.viewport) return;
    clearTimeout(this._scrollT);
    this._scrollT = setTimeout(() => {
      const { perPage, pages, step } = this.metrics();
      const pageW = perPage * step;
      if (pageW <= 0) return;
      const maxScroll = vp.scrollWidth - vp.clientWidth;
      // Snap the reported page to the last one once scrolled to the very end,
      // so a partial last page still marks the final dot + disables Next.
      const pg = vp.scrollLeft >= maxScroll - 2 ? pages - 1 : Math.round(vp.scrollLeft / pageW);
      this.page = Math.min(Math.max(pg, 0), pages - 1);
      this.updateControls(pages);
    }, 60);
  }

  layout() {
    const { pages } = this.metrics();
    this.buildDots(pages);
    this.go(Math.min(this.page, pages - 1), false);
  }

  selectTab(i) {
    this.active = i;
    this.page = 0;
    this.tabs?.forEach((t, idx) => {
      const on = idx === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    this.panels.forEach((p, idx) => {
      const on = idx === i;
      p.classList.toggle('is-active', on);
      p.hidden = !on;
    });
    // Snap to the first page without animating across the tab change.
    const track = this.track;
    if (track) { track.style.transition = 'none'; track.style.transform = 'translateX(0)'; void track.offsetWidth; track.style.transition = ''; }
    const vp = this.viewport;
    if (vp) vp.scrollLeft = 0; // reset the native scroller on mobile too
    this.layout();
  }

  buildDots(pages) {
    if (!this.dotsWrap) return;
    this.dotsWrap.innerHTML = '';
    this.dots = [];
    for (let i = 0; i < pages; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'showcase__dot';
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.addEventListener('click', () => this.go(i));
      this.dotsWrap.appendChild(dot);
      this.dots.push(dot);
    }
    this.dotsWrap.style.display = pages <= 1 ? 'none' : '';
  }

  updateControls(pages) {
    if (this.prevBtn) this.prevBtn.disabled = this.page <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.page >= pages - 1;
    // Dynamic (windowed) dots — matches the live carousel: with many pages, only a
    // sliding window around the active dot is shown (active ± WINDOW), the outer
    // dot scaled down, the rest hidden. With few pages (<= WINDOW*2) show them all.
    const WINDOW = 2;
    const dynamic = pages > WINDOW + 1;
    this.dots?.forEach((d, idx) => {
      d.setAttribute('aria-current', idx === this.page ? 'true' : 'false');
      if (!dynamic) { d.style.display = ''; d.classList.remove('showcase__dot--sm'); return; }
      const dist = Math.abs(idx - this.page);
      if (dist > WINDOW) { d.style.display = 'none'; d.classList.remove('showcase__dot--sm'); }
      else { d.style.display = ''; d.classList.toggle('showcase__dot--sm', dist === WINDOW); }
    });
  }
}

if (!customElements.get('custom-product-showcase')) {
  customElements.define('custom-product-showcase', CustomProductShowcase);
}
